import { z } from "zod";
// ============================================================================
// AI Chat Enums
// ============================================================================
export const AIChatRoleEnum = z.enum(["user", "assistant"]);
// ============================================================================
// AI Chat Message
// ============================================================================
export const AIChatMessageSchema = z.object({
    message_id: z.string(),
    role: AIChatRoleEnum,
    content: z.string(),
    created_at: z.string(),
    prompt_tokens: z.number().optional(),
    completion_tokens: z.number().optional(),
    total_tokens: z.number().optional(),
    context: z
        .object({
        exercise_id: z.number().optional(),
        exercise_name: z.string().optional(),
        exercise_type: z.string().optional(),
    })
        .optional(),
});
// ============================================================================
// AI Chat Session
// ============================================================================
export const AIChatSessionSchema = z.object({
    user_uid: z.string(),
    module_id: z.number(),
    course_id: z.number(),
    module_name: z.string().nullable().optional(),
    module_slug: z.string().nullable().optional(),
    course_name: z.string().nullable().optional(),
    course_slug: z.string().nullable().optional(),
    message_count: z.number().default(0),
    last_message_text: z.string().nullable().optional(),
    last_message_at: z.string().nullable().optional(),
    total_tokens_used: z.number().default(0),
    created_at: z.string(),
    updated_at: z.string(),
});
// ============================================================================
// AI Chat Usage
// ============================================================================
export const AIChatUsageSchema = z.object({
    user_uid: z.string(),
    month: z.string(), // YYYY-MM
    total_tokens: z.number().default(0),
    prompt_tokens: z.number().default(0),
    completion_tokens: z.number().default(0),
    request_count: z.number().default(0),
    estimated_cost_usd: z.number().default(0),
});
// ============================================================================
// AI User Memory
// ============================================================================
export const AIChatMemorySchema = z.object({
    profile: z
        .object({
        topics_studied: z
            .array(z.object({
            module_id: z.number(),
            module_name: z.string(),
            course_name: z.string(),
            completion_pct: z.number(),
            last_accessed: z.string(),
        }))
            .default([]),
        common_mistakes: z
            .array(z.object({
            category: z.string(),
            description: z.string(),
            frequency: z.number(),
            last_seen: z.string(),
            examples: z.array(z.string()).default([]),
        }))
            .default([]),
        strengths: z.array(z.string()).default([]),
        preferences: z
            .object({
            prefers_explanations_in: z.string().optional(),
            response_length: z.enum(["short", "medium", "long"]).optional(),
        })
            .optional(),
    })
        .optional(),
    summary: z.string().optional(),
    updated_at: z.string().optional(),
});
// ============================================================================
// API Payloads
// ============================================================================
export const SendAIChatMessagePayloadSchema = z.object({
    content: z.string().min(1).max(2000),
    exercise_context: z
        .object({
        exercise_id: z.number(),
        exercise_name: z.string(),
        exercise_type: z.string(),
        instructions: z.string().optional(),
    })
        .optional(),
});
// ============================================================================
// API Response Types
// ============================================================================
export const AIChatUsageResponseSchema = z.object({
    tokens_used: z.number(),
    tokens_limit: z.number(),
    requests_used: z.number(),
    requests_limit: z.number(),
    limit_reached: z.boolean(),
    tier: z.string(),
    month: z.string(),
});
export const AIChatHistoryResponseSchema = z.object({
    session: AIChatSessionSchema.nullable(),
    messages: z.array(AIChatMessageSchema),
    lastEvaluatedKey: z.record(z.string(), z.unknown()).optional(),
});
export const AIChatSessionsResponseSchema = z.object({
    sessions: z.array(AIChatSessionSchema),
    lastEvaluatedKey: z.record(z.string(), z.unknown()).optional(),
});
// ============================================================================
// Usage Limits
// ============================================================================
export const AI_CHAT_LIMITS = {
    FREE: {
        // Raised 10x on 2026-09-17 (Yehor) once the tutor became book-grounded.
        // A grounded answer costs ~5,000 tokens — the book passages are the bulk of
        // it — so the old 50,000 bought about ten messages a month, which is not a
        // free tier, it is a demo. At 500,000 the token budget and the 100-request
        // cap land in the same place: ~100 messages either way, so neither limit
        // bites before the other and a student hits a number that means something.
        tokens_per_month: 500_000,
        requests_per_month: 100,
        max_message_length: 2_000,
    },
    PREMIUM: {
        tokens_per_month: 7_500_000,
        requests_per_month: 1_000,
        max_message_length: 2_000,
    },
};
